import { useState, type FormEvent, type ReactNode } from 'react'

import { Avatar, Button, ErrorBanner, Field, Input, Modal, cn, useRoster } from '@/features/issue'
import type { MemberRow } from '@/features/issue'

import { useChatMutations } from './hooks'
import type { ChatChannel } from './types'

type Mode = 'dm' | 'group'

/**
 * Start a direct message or a group. Both pick from the workspace roster,
 * which is exactly the set the server will accept.
 */
export function NewConversationModal({
  open,
  onClose,
  slug,
  currentUserId,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  slug: string
  currentUserId: number
  onCreated: (channel: ChatChannel) => void
}) {
  const [mode, setMode] = useState<Mode>('dm')
  const [search, setSearch] = useState('')
  const [name, setName] = useState('')
  const [picked, setPicked] = useState<Set<number>>(() => new Set())
  const roster = useRoster(slug)
  const { createDm, createGroup } = useChatMutations(slug)

  const candidates = roster.members.filter((member) => {
    if (member.user_id === currentUserId || !member.user) return false
    const term = search.trim().toLowerCase()
    if (!term) return true
    return member.user.name.toLowerCase().includes(term) || member.user.email.toLowerCase().includes(term)
  })

  const reset = () => {
    setMode('dm')
    setSearch('')
    setName('')
    setPicked(new Set())
    createDm.reset()
    createGroup.reset()
  }
  const close = () => {
    reset()
    onClose()
  }

  const startDm = (member: MemberRow) => {
    createDm.mutate(member.user_id, {
      onSuccess: (channel) => {
        reset()
        onCreated(channel)
      },
    })
  }

  const toggle = (id: number) =>
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const submitGroup = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return
    createGroup.mutate(
      { name: name.trim(), member_ids: Array.from(picked) },
      {
        onSuccess: (channel) => {
          reset()
          onCreated(channel)
        },
      },
    )
  }

  const pending = createDm.isPending || createGroup.isPending

  return (
    <Modal open={open} onClose={close} title="New message" description="Message a teammate directly or start a group.">
      <div className="space-y-4">
        <div role="tablist" aria-label="Conversation type" className="flex gap-1 rounded-md bg-ink-100 p-1 dark:bg-ink-800">
          {(['dm', 'group'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              onClick={() => setMode(value)}
              className={cn(
                'flex-1 rounded px-3 py-1.5 text-sm font-medium transition',
                mode === value
                  ? 'bg-white text-ink-900 shadow-sm dark:bg-ink-900 dark:text-ink-50'
                  : 'text-ink-600 hover:text-ink-900 dark:text-ink-300 dark:hover:text-ink-100',
              )}
            >
              {value === 'dm' ? 'Direct message' : 'Group'}
            </button>
          ))}
        </div>

        <ErrorBanner error={createDm.error ?? createGroup.error} />

        {mode === 'group' ? (
          <form onSubmit={submitGroup} className="space-y-3">
            <Field label="Group name" htmlFor="chat-group-name">
              <Input
                id="chat-group-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Launch week"
                maxLength={80}
                autoComplete="off"
              />
            </Field>
            <Field label="Members" htmlFor="chat-group-search" hint="You are added automatically.">
              <Input
                id="chat-group-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search teammates"
                autoComplete="off"
              />
            </Field>
            <MemberPicker
              members={candidates}
              isLoading={roster.isLoading}
              render={(member) => (
                <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-ink-100 dark:hover:bg-ink-800">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-ink-300 accent-brand-600"
                    checked={picked.has(member.user_id)}
                    onChange={() => toggle(member.user_id)}
                  />
                  <Avatar user={member.user} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm">{member.user?.name}</span>
                </label>
              )}
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={close} disabled={pending}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!name.trim() || pending}>
                {createGroup.isPending ? 'Creating…' : 'Create group'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <Field label="To" htmlFor="chat-dm-search">
              <Input
                id="chat-dm-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search teammates"
                autoComplete="off"
              />
            </Field>
            <MemberPicker
              members={candidates}
              isLoading={roster.isLoading}
              render={(member) => (
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => startDm(member)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition hover:bg-ink-100 disabled:opacity-60 dark:hover:bg-ink-800"
                >
                  <Avatar user={member.user} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{member.user?.name}</span>
                    <span className="block truncate text-xs text-ink-500">{member.user?.email}</span>
                  </span>
                </button>
              )}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

function MemberPicker({
  members,
  isLoading,
  render,
}: {
  members: MemberRow[]
  isLoading: boolean
  render: (member: MemberRow) => ReactNode
}) {
  return (
    <div className="max-h-64 overflow-y-auto rounded-md border border-ink-200 p-1 dark:border-ink-800">
      {isLoading ? (
        <p className="px-2 py-3 text-sm text-ink-500">Loading teammates…</p>
      ) : members.length === 0 ? (
        <p className="px-2 py-3 text-sm text-ink-500">No teammates match. Invite people from Settings → Members.</p>
      ) : (
        <ul className="space-y-0.5">
          {members.map((member) => (
            <li key={member.user_id}>{render(member)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
